import React, { memo } from 'react';
import { Send, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { holySites } from '../mockData';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { holySitePageBySiteId } from '../data/holySitePages';

// Routes to dedicated topical landing pages for specific sites. Boosts both
// SEO (internal link juice) and UX ("learn more" for high-intent visitors).
const SITE_LANDING_PAGES = {
  1: '/pray-at-the-church-of-the-holy-sepulchre',
  ...holySitePageBySiteId,
};

// Memoized site card for better performance
const SiteCard = memo(({ site, t }) => (
  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group bg-white">
    <div className="relative h-64 overflow-hidden">
      <img 
        src={site.imageUrl} 
        alt={site.name}
        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute top-4 right-4">
        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-700 capitalize">
          {site.category}
        </span>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <h3 className="text-white font-bold text-xl mb-1">{site.name}</h3>
        <div className="flex items-center text-white/80 text-sm">
          <MapPin className="w-4 h-4 mr-1" />
          <span>{t('sacredSites.jerusalem')}</span>
        </div>
      </div>
    </div>
    <CardContent className="p-6">
      <p className="text-gray-600 mb-4">{site.description}</p>
      <p className="text-amber-600 text-sm font-medium mb-4">{site.significance}</p>
      <Link to={`/prayer-request?site=${site.id}`}>
        <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
          <Send className="w-4 h-4 mr-2" />
          {t('sacredSites.sendPrayer')}
        </Button>
      </Link>
      {SITE_LANDING_PAGES[site.id] && (
        <Link
          to={SITE_LANDING_PAGES[site.id]}
          className="block mt-3 text-center text-sm font-semibold text-amber-700 hover:text-amber-800 underline underline-offset-2"
          data-testid={`site-learn-more-${site.id}`}
        >
          Learn more about this site →
        </Link>
      )}
    </CardContent>
  </Card>
));

const SacredSites = () => {
  const { t } = useLanguage();

  const filteredSites = holySites;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO title="Church of the Holy Sepulchre, Western Wall & Jerusalem's Holy Sites" description="Explore the sacred sites where your prayer is delivered — the Church of the Holy Sepulchre, Western Wall, Garden Tomb, Via Dolorosa, Church of All Nations, and more of Jerusalem's holiest places." path="/sacred-sites" />
      {/* Hero Section */}
      <div 
        className="relative h-96 mb-16"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1587297069400-6cc5da81658a)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/60" />
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-center">
          <div className="max-w-3xl text-center">
            <div className="flex justify-center mb-6">
              <div className="rounded-full bg-white/95 backdrop-blur-md border-2 border-white shadow-2xl overflow-hidden">
                <img 
                  src="/logo-circle.png"
                  alt="Jerusalem Holy Prayer"
                  className="h-16 w-16 object-cover"
                />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {t('sacredSites.title')}
            </h1>
            <p className="text-xl text-gray-200">
              {t('sacredSitesPage.heroSubtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* All sites are in Jerusalem — no filter needed */}

        {/* Sites Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {filteredSites.map((site) => (
            <Card key={site.id} className="overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img
                  src={site.image}
                  alt={site.name}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-1">{site.name}</h3>
                  <div className="flex items-center text-amber-400 text-sm font-medium">
                    <MapPin size={14} className="mr-1" />
                    {site.location}
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-gray-600 mb-6 leading-relaxed">{site.description}</p>
                <Link to={`/prayer-request?site=${site.id}`}>
                  <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                    <Send className="mr-2" size={16} />
                    {t('sacredSitesPage.sendPrayerHere')}
                  </Button>
                </Link>
                {SITE_LANDING_PAGES[site.id] && (
                  <Link
                    to={SITE_LANDING_PAGES[site.id]}
                    className="block mt-3 text-center text-sm font-semibold text-amber-700 hover:text-amber-800 underline underline-offset-2"
                    data-testid={`site-learn-more-${site.id}`}
                  >
                    Learn more about the {site.name.replace(/^The /, '')} →
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SacredSites;